// Google Analytics Service for comprehensive tracking
// IMPORTANT: Replace GA_MEASUREMENT_ID in index.html with your actual GA4 Measurement ID

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

export type WizardStep = 
  | 'intro'
  | 'platform'
  | 'league-input'
  | 'team-select'
  | 'host-select'
  | 'lead-capture'
  | 'call'
  | 'ended'
  | 'espn-private-league';

export type Platform = 'espn' | 'sleeper' | 'yahoo' | 'direct' | 'manual' | null;

export type ClickCategory = 
  | 'navigation'
  | 'form'
  | 'social'
  | 'call_action'
  | 'share'
  | 'external_link';

interface UserJourneyData {
  step: WizardStep;
  platform?: Platform;
  host?: string;
  teamName?: string;
  leagueId?: string;
  timestamp: number;
}

class AnalyticsService {
  private userJourney: UserJourneyData[] = [];
  private sessionStartTime: number;
  private userId: string;

  constructor() {
    this.sessionStartTime = Date.now();
    this.userId = this.getOrCreateUserId();
    this.initializeSession();
  }

  private getOrCreateUserId(): string {
    let userId = localStorage.getItem('analytics_user_id');
    if (!userId) {
      userId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('analytics_user_id', userId);
    }
    return userId;
  }

  private initializeSession() {
    this.trackEvent('session_start', {
      user_id: this.userId,
      session_start_time: this.sessionStartTime,
      landing_page: 'fantasy_football_ai_podcast',
      referrer: document.referrer || 'direct',
      utm_source: this.getUrlParam('utm_source'),
      utm_medium: this.getUrlParam('utm_medium'),
      utm_campaign: this.getUrlParam('utm_campaign'),
      utm_content: this.getUrlParam('utm_content'),
      utm_term: this.getUrlParam('utm_term')
    });
  }

  private getUrlParam(param: string): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(param);
  }

  // Track wizard step progression
  trackWizardStep(step: WizardStep, additionalData?: any) {
    const stepData = {
      step,
      timestamp: Date.now(),
      ...additionalData
    };

    this.userJourney.push(stepData);

    this.trackEvent('wizard_step', {
      step_name: step,
      step_number: this.getStepNumber(step),
      total_steps: 7,
      time_on_previous_step: this.getTimeOnPreviousStep(),
      journey_path: this.getJourneyPath(),
      ...additionalData
    });

    // Track specific page view for each step
    this.trackPageView(`/fantasy-football/${step}`, {
      wizard_step: step,
      user_journey_length: this.userJourney.length
    });
  }

  private getStepNumber(step: WizardStep): number {
    const steps: WizardStep[] = [
      'intro', 'platform', 'league-input', 
      'team-select', 'host-select', 'lead-capture', 
      'call', 'ended', 'espn-private-league'
    ];
    return steps.indexOf(step) + 1;
  }

  private getTimeOnPreviousStep(): number {
    if (this.userJourney.length < 2) return 0;
    const current = this.userJourney[this.userJourney.length - 1];
    const previous = this.userJourney[this.userJourney.length - 2];
    return (current.timestamp - previous.timestamp) / 1000; // in seconds
  }

  private getJourneyPath(): string {
    return this.userJourney.map(j => j.step).join(' -> ');
  }

  // Track platform selection
  trackPlatformSelection(platform: Platform) {
    this.trackEvent('platform_selected', {
      platform,
      step: 'platform',
      user_id: this.userId
    });
  }

  // Track league data loading
  trackLeagueLoad(platform: Platform, success: boolean, leagueId?: string, error?: string) {
    this.trackEvent('league_load_attempt', {
      platform,
      success,
      league_id: leagueId,
      error_message: error,
      attempt_number: this.getLeagueLoadAttempts(platform)
    });
  }

  private getLeagueLoadAttempts(platform: Platform): number {
    const key = `league_attempts_${platform}`;
    const attempts = parseInt(sessionStorage.getItem(key) || '0') + 1;
    sessionStorage.setItem(key, attempts.toString());
    return attempts;
  }

  // Track team selection
  trackTeamSelection(teamName: string, rosterSize: number) {
    this.trackEvent('team_selected', {
      team_name: teamName,
      roster_size: rosterSize,
      selection_time: (Date.now() - this.sessionStartTime) / 1000
    });
  }

  // Track host selection
  trackHostSelection(host: string) {
    this.trackEvent('host_selected', {
      host_name: host,
      selection_time: (Date.now() - this.sessionStartTime) / 1000
    });
  }

  // Track lead form interactions
  trackLeadFormSubmit(formData: any) {
    this.trackEvent('lead_form_submitted', {
      has_name: !!formData.name,
      has_email: !!formData.email,
      has_phone: !!formData.phone,
      consent_given: formData.consent,
      is_returning_user: !!localStorage.getItem('yobi_fantasy_user_info'),
      form_completion_time: (Date.now() - this.sessionStartTime) / 1000
    });
  }

  // Track call events
  trackCallStart(agentId: string, host: string) {
    this.trackEvent('call_started', {
      agent_id: agentId,
      host,
      journey_completion: this.getJourneyPath(),
      time_to_call: (Date.now() - this.sessionStartTime) / 1000
    });
  }

  trackCallEnd(duration: number, wasCompleted: boolean) {
    this.trackEvent('call_ended', {
      call_duration: duration,
      was_completed: wasCompleted,
      total_session_time: (Date.now() - this.sessionStartTime) / 1000
    });
  }

  trackRecordingFetched(success: boolean, attempts: number) {
    this.trackEvent('recording_fetched', {
      success,
      fetch_attempts: attempts,
      time_since_call_end: this.getTimeSinceLastEvent('call_ended')
    });
  }

  private getTimeSinceLastEvent(eventName: string): number | null {
    // This would need to be implemented based on your event tracking
    return null;
  }

  // Track clicks with detailed attribution
  trackClick(
    elementName: string, 
    category: ClickCategory,
    additionalData?: any
  ) {
    this.trackEvent('element_clicked', {
      element_name: elementName,
      click_category: category,
      current_step: this.getCurrentStep(),
      click_path: this.getClickPath(),
      ...additionalData
    });
  }

  private getCurrentStep(): WizardStep | null {
    if (this.userJourney.length === 0) return null;
    return this.userJourney[this.userJourney.length - 1].step;
  }

  private getClickPath(): string {
    // Track the sequence of clicks in the session
    const clicks = sessionStorage.getItem('click_path') || '';
    return clicks;
  }

  // Track social shares
  trackSocialShare(platform: string, location: 'during_call' | 'after_call') {
    this.trackEvent('social_share', {
      platform,
      share_location: location,
      current_step: this.getCurrentStep(),
      session_duration: (Date.now() - this.sessionStartTime) / 1000
    });
  }

  // Track errors
  trackError(errorType: string, errorMessage: string, context?: any) {
    this.trackEvent('error_occurred', {
      error_type: errorType,
      error_message: errorMessage,
      current_step: this.getCurrentStep(),
      journey_path: this.getJourneyPath(),
      ...context
    });
  }

  // Core tracking methods
  private trackEvent(eventName: string, parameters?: any) {
    if (typeof window.gtag !== 'function') {
      console.warn('Google Analytics not loaded. Event not tracked:', eventName, parameters);
      return;
    }

    // Add common parameters to all events
    const enrichedParams = {
      ...parameters,
      user_id: this.userId,
      session_id: this.sessionStartTime,
      page_type: 'fantasy_football_landing',
      timestamp: new Date().toISOString()
    };

    window.gtag('event', eventName, enrichedParams);
    
    // Also log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`📊 GA Event: ${eventName}`, enrichedParams);
    }
  }

  trackPageView(pagePath: string, additionalData?: any) {
    if (typeof window.gtag !== 'function') {
      console.warn('Google Analytics not loaded. Page view not tracked:', pagePath);
      return;
    }

    window.gtag('event', 'page_view', {
      page_path: pagePath,
      page_title: this.getPageTitle(pagePath),
      page_location: window.location.origin + pagePath,
      user_id: this.userId,
      ...additionalData
    });
  }

  private getPageTitle(pagePath: string): string {
    const titles: { [key: string]: string } = {
      '/fantasy-football/intro': 'FF Landing - Introduction',
      '/fantasy-football/platform': 'FF Landing - Platform Selection',
      '/fantasy-football/league-input': 'FF Landing - League Input',
      '/fantasy-football/team-select': 'FF Landing - Team Selection',
      '/fantasy-football/host-select': 'FF Landing - Host Selection',
      '/fantasy-football/lead-capture': 'FF Landing - Lead Capture',
      '/fantasy-football/call': 'FF Landing - AI Call Active',
      '/fantasy-football/ended': 'FF Landing - Call Ended'
    };
    return titles[pagePath] || 'Fantasy Football AI Podcast';
  }

  // Track conversion funnel
  trackConversion(conversionType: 'lead_captured' | 'call_initiated' | 'call_completed') {
    this.trackEvent('conversion', {
      conversion_type: conversionType,
      conversion_value: this.getConversionValue(conversionType),
      journey_path: this.getJourneyPath(),
      total_session_time: (Date.now() - this.sessionStartTime) / 1000,
      platform: this.userJourney.find(j => j.platform)?.platform
    });
  }

  private getConversionValue(conversionType: string): number {
    const values: { [key: string]: number } = {
      'lead_captured': 10,
      'call_initiated': 25,
      'call_completed': 50
    };
    return values[conversionType] || 0;
  }

  // Get complete journey data for debugging or sending to backend
  getJourneyData() {
    return {
      user_id: this.userId,
      session_start: this.sessionStartTime,
      session_duration: (Date.now() - this.sessionStartTime) / 1000,
      journey: this.userJourney,
      journey_path: this.getJourneyPath(),
      current_step: this.getCurrentStep()
    };
  }
}

// Create singleton instance
export const analytics = new AnalyticsService();

// Export convenience methods
export const trackWizardStep = (step: WizardStep, data?: any) => 
  analytics.trackWizardStep(step, data);

export const trackClick = (
  elementName: string, 
  category: ClickCategory, 
  data?: any
) => analytics.trackClick(elementName, category, data);

export const trackError = (
  errorType: string, 
  errorMessage: string, 
  context?: any
) => analytics.trackError(errorType, errorMessage, context);

export const trackSocialShare = (
  platform: string, 
  location: 'during_call' | 'after_call'
) => analytics.trackSocialShare(platform, location);
